import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { sortBy, isEmpty } from 'lodash';

import FormGroupWithSelect from '@reverbdotcom/commons/src/components/form_group_with_select';
import { APIRequest } from '@reverbdotcom/discovery-ui';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';

import UrlHelpers from '../shared/url_helpers';

const SPECIFIED_CATEGORY_UUIDS = [
  'dfd39027-d134-4353-b9e4-57dc6be791b9', // Electric Guitars
  '80d6ce96-487c-4ac1-ad10-d4bef6336fe6', // 12-string
  '367e1d5d-1185-4a1e-b283-8ec860dc1d5f', // Archtop
  '8f8b1f88-83f5-449c-8c93-b57cf3ba1fd7', // Baritone
  '5db35d7e-2b7e-4dcf-a73b-6a144c710956', // Hollow Body
  'ddd7553e-68d5-4005-a356-3f94202682a8', // Lap Steel
  'c86f64cf-43df-4430-a2bf-b4126d81c5bd', // Left-Handed
  '1c7938ae-08d1-4337-b064-ef278869011b', // Pedal Steel
  '6a63ac2e-f2a5-4064-b6ea-0393f42ee497', // Semi-Hollow
  'e57deb7a-382b-4e18-a008-67d4fbcb2879', // Solid Body
  '69edc7c9-a145-4e7d-bab2-26e9df982c57', // Tenor
  '60a89ea2-9604-46a8-a113-bfc065b48f28', // Travel/Mini
  'dfb72735-b7a3-4c8f-9b14-0d1dd904af1e', // Interfaces
  '0f2bbf76-3225-44d5-8a5b-c540cc1fd058', // Microphones
];

const TRAIT_SELECT = 'TraitSelect';

const ORDERED_ELECTRIC_GUITAR_TRAITS = [
  'b05df9b7-a6c9-4230-8c67-b32f530b77b4', // Body Type
  'c78557ac-fc14-46bc-abce-5c746680e7b2', // Right/Left Handed
  '64dbf67b-2fa8-48f7-9762-c6b445ac6a46', // Number of Strings
  '9a355c89-c810-446e-be9f-0def8ef9dad5', // Body Shape
  'c0b03e40-f463-433c-838e-e1954c730d82', // Wood Top Style
  '5328712b-4b10-434e-9f79-34c9984ffac3', // Finish Featured
  '024c6bde-82c6-4c53-8cf2-93a270cdde2b', // Finish Style
  '8efe834a-c390-4adf-a59c-46fed3b84891', // Frets
  '6a24e2dc-800d-4f06-ac67-acef9569a40f', // Fretboard Material
  '7bd31a32-a731-46cf-8526-1e44cc081e87', // Body Material
  'cd0caecf-798d-4315-a1dd-691e2550d3d1', // Top Material
  '4480838c-03d7-425a-9fd0-386dddc38fbf', // Neck Material
  '2057958f-5985-4e43-bcb8-8cbd4e88c322', // Offset Body
  '00579c3a-c5f2-49c4-8498-1f2ff993ccf3', // Scale Length
  '971f638c-e6e1-4363-b2d6-b9a06838777e', // Nut Width
  '782ee63b-1741-422b-950b-0004506711ef', // Bridge/Tailpiece Type
  'ca5f02bf-4127-4a5c-be41-8603cd904ffb', // Pickup Configuration
  'f1967c7b-1287-4742-ac07-7557ffa3efc0', // Neck Constriction
  '0c3dfa03-e905-4883-afce-f15ffb9d8975', // Fretboard Radius
];

class TraitSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      traits: [],
      loading: false,
      expanded: false,
    };

    this.renderSelect = this.renderSelect.bind(this);
  }

  isOnSpecifiedCategory() {
    const { categoryUuids } = this.props;

    if (isEmpty(categoryUuids)) { return null; }

    return categoryUuids.some(uuid => SPECIFIED_CATEGORY_UUIDS.includes(uuid));
  }

  getCategoryUuid() {
    return this.props.categoryUuids[0];
  }

  isEditingUnmatchedListing() {
    return this.props.persistedForm && !this.props.hasCanonicalProduct;
  }

  fetchTraits() {
    if (this.isOnSpecifiedCategory()) {
      this.setState({ loading: true });

      APIRequest.V3.get(
        UrlHelpers.categoriesWithTraitsPath,
        { category_uuids: this.getCategoryUuid() },
      ).then(data => {
        this.setState({
          traits: data.traits.filter(({ values }) => values.length > 0),
          loading: false,
        });
      });
    }
  }

  orderTraits(traits) {
    return sortBy(traits, (trait) => {
      return ORDERED_ELECTRIC_GUITAR_TRAITS.indexOf(trait.uuid);
    });
  }

  getFormType() {
    if (this.props.blankForm) {
      return 'Blank Form';
    }

    if (this.props.persistedForm) {
      return 'Persisted Form';
    }

    return 'Seeded Form';
  }

  trackView() {
    if (this.isOnSpecifiedCategory()) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: TRAIT_SELECT,
        displayStyle: this.getFormType(),
      });
    }
  }

  componentDidMount() {
    this.trackView();

    if (this.props.blankForm && !isEmpty(this.props.categoryUuids)) {
      this.fetchTraits();
    }

    this.setState({ traits: this.props.traits });
  }

  componentDidUpdate(prevProps) {
    if (this.props.categoryUuids !== prevProps.categoryUuids) {
      if (this.props.blankForm || this.isEditingUnmatchedListing()) {
        this.fetchTraits();
        this.trackView();
      }

      this.setState({ traits: [] });
    }
  }

  getOptions(trait) {
    if (trait.disabled) {
      return trait.values.map(value => (
        {
          value: value.uuid,
          text: value.name,
          disabled: true,
        }
      ));
    }

    return [{
      value: '',
      text: I18n.t('listingForm.traitSelect.optionPlaceholder'),
    }].concat(trait.values.map(value => (
      {
        value: value.uuid,
        text: value.name,
      }
    )));
  }

  handleChange({ newValue, uuid }) {
    this.setState(prevState => {
      const traits = [...prevState.traits];
      const idx = traits.findIndex(t => t.uuid === uuid);
      traits[idx] = { ...traits[idx], selected: newValue };
      return { traits };
    });
  }

  renderExpandButton() {
    const { expanded } = this.state;

    return (
      <div className="trait-select__expand">
        <button
          type="button"
          className="button button--small"
          onClick={() => this.setState({ expanded: !expanded })}
        >
          {I18n.t(`listingForm.traitSelect.toggle.${expanded ? 'hide' : 'show'}`)}
        </button>
      </div>
    );
  }

  renderSelect(trait) {
    const value = trait.values.find(val => val.name === trait.selected || val.uuid === trait.selected);

    return (
      <div className="trait-select__field" key={trait.uuid}>
        <input type="hidden" name="product[traits][][uuid]" value={trait.uuid} />
        <FormGroupWithSelect
          label={trait.name}
          inputName="product[traits][][trait_value]"
          fieldName="newValue"
          updateField={({ newValue }) => this.handleChange({ newValue, uuid: trait.uuid })}
          id={trait.uuid}
          disabled={trait.disabled}
          options={this.getOptions(trait)}
          value={value && value.uuid}
        />
      </div>
    );
  }

  render() {
    if (isEmpty(this.state.traits) || this.state.loading) { return null; }

    const classes = classNames(
      'trait-select',
      { 'trait-select--expanded': this.state.expanded },
    );

    return (
      <div className={classes}>
        <div className="mb-1">
          <b className="d-block" data-trait-select-add-tech-specs>
            {I18n.t('listingForm.traitSelect.addTechSpecs')}
            <span className="form-group__tag ml-space">
              {I18n.t('listingForm.traitSelect.optional')}
            </span>
          </b>
          {I18n.t('listingForm.traitSelect.traitSelectDescription')}
        </div>
        <div className="trait-select__fields">
          {this.orderTraits(this.state.traits).map(this.renderSelect)}
        </div>
        {this.renderExpandButton()}
      </div>
    );
  }
}

TraitSelect.propTypes = {
  traits: PropTypes.arrayOf(PropTypes.shape({})),
  categoryUuids: PropTypes.arrayOf(PropTypes.string),
  blankForm: PropTypes.bool,
  persistedForm: PropTypes.bool,
  hasCanonicalProduct: PropTypes.bool,
};

export default withUserContext(TraitSelect);
